import React, { useEffect, useRef, useState, type DragEvent } from "react";
import {
  Upload,
  FileText,
  Trash2,
  Undo2,
  Loader2,
  RefreshCw,
  GripVertical,
  MessageSquarePlus,
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import {
  CUSTOM_LABEL_OPTION,
  DOC_SECTIONS,
  DOC_TYPE_LABELS,
  DOC_TYPES,
  DocType,
  DocTypeChoice,
  TrackedFileDocument,
} from "@/types/statement";
import { CustomDocName } from "@/components/application/CustomDocName";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatFileSize } from "@/utils/formatFileSize";
import {
  endDocumentDrag,
  startDocumentDrag,
  type DraggedDocument,
} from "@/agent/chat/documentDrag";
import { addToLip } from "@/agent/lip/lipStore";

/** Devices that drag; on a phone a row is not draggable and its Add to chat button covers it. */
const FINE_POINTER = "(hover: hover) and (pointer: fine)";
/** A row's own controls, which are pressed and never start the row's drag. */
const ROW_ACTION = "[data-row-action]";

function useFinePointer(): boolean {
  const [fine, setFine] = useState(() => window.matchMedia?.(FINE_POINTER).matches ?? true);
  useEffect(() => {
    const query = window.matchMedia?.(FINE_POINTER);
    if (!query) return;
    const changed = () => setFine(query.matches);
    query.addEventListener("change", changed);
    return () => query.removeEventListener("change", changed);
  }, []);
  return fine;
}

const isRowAction = (target: EventTarget | null) =>
  target instanceof Element && target.closest(ROW_ACTION) !== null;

const STATUS_CLASS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  uploading: "bg-primary/15 text-primary",
  processing: "bg-primary/15 text-primary",
  completed: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
  processed: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
  failed: "bg-destructive/15 text-destructive",
  extraction_failed: "bg-destructive/15 text-destructive",
};

/** What the app's documents upload (upload-statements) takes, as react-dropzone's `accept`. */
export const STATEMENT_FILE_ACCEPT = {
  "application/pdf": [".pdf"],
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
  "application/x-ofx": [".ofx"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const MAX_FILENAME_LENGTH = 100;
const truncateFilename = (name: string): string =>
  name.length > MAX_FILENAME_LENGTH ? name.slice(0, MAX_FILENAME_LENGTH) + "…" : name;

/** TrackedFile uses ``processed``; bank statements use ``completed``. Show one label. */
const displayStatus = (status: string): string => (status === "processed" ? "completed" : status);

interface AppFileManagerProps {
  borrowerId: number | null;
  documents?: TrackedFileDocument[];
  uploading?: boolean;
  uploadError?: string | null;
  onDrop: (files: File[]) => void;
  onView?: (documentId: number) => void;
  pendingFiles?: File[];
  pendingDeletes?: number[];
  pendingDocTypes?: Record<number, DocType>;
  hasPendingChanges?: boolean;
  onRemovePendingFile?: (index: number) => void;
  onAddPendingDelete?: (statementId: number) => void;
  onRemovePendingDelete?: (statementId: number) => void;
  onDocTypeChange?: (documentId: number, docType: DocTypeChoice) => void;
  onReprocess?: (statementId: number) => void;
  onSave?: () => void;
  onDiscard?: () => void;
  /** The user has the assistant: each document is a card dragged (or added) into its chat. */
  canAddToChat?: boolean;
  /** Plaid asset report row (Application tab when plaid_completed). */
  plaidAssetReport?: {
    visible: boolean;
    fileUrl: string | null;
    pdfFetchFailed: boolean;
    retrying: boolean;
    onView: () => void;
    onRetry: () => void;
  };
}

const AppFileManager: React.FC<AppFileManagerProps> = ({
  borrowerId,
  documents = [],
  uploading = false,
  uploadError,
  onDrop,
  onView,
  pendingFiles = [],
  pendingDeletes = [],
  pendingDocTypes = {},
  hasPendingChanges = false,
  onRemovePendingFile,
  onAddPendingDelete,
  onRemovePendingDelete,
  onDocTypeChange,
  onReprocess,
  onSave,
  onDiscard,
  canAddToChat = false,
  plaidAssetReport,
}) => {
  const finePointer = useFinePointer();
  // Whether the press that may start a drag was on one of the row's controls.
  const pressedAction = useRef(false);
  // The frame that turns the lifted row into the dimmed one left on the page.
  const dragFrame = useRef<number | null>(null);

  // The card itself, lifted, is the drag image; a drag from one of its controls never starts.
  const startRowDrag = (event: DragEvent<HTMLLIElement>, document: DraggedDocument) => {
    if (pressedAction.current || isRowAction(event.target)) {
      event.preventDefault();
      return;
    }
    const row = event.currentTarget;
    startDocumentDrag(event, document);
    row.dataset.lifted = "";
    const box = row.getBoundingClientRect();
    event.dataTransfer.setDragImage?.(row, event.clientX - box.left, event.clientY - box.top);
    // Taken after the image: the row left on the page reads as the one being moved.
    dragFrame.current = requestAnimationFrame(() => {
      dragFrame.current = null;
      delete row.dataset.lifted;
      row.dataset.dragging = "";
    });
  };

  const endRowDrag = (event: DragEvent<HTMLLIElement>) => {
    // A drag that ends within its first frame must not get the dimmed look put back afterwards.
    if (dragFrame.current != null) cancelAnimationFrame(dragFrame.current);
    dragFrame.current = null;
    delete event.currentTarget.dataset.lifted;
    delete event.currentTarget.dataset.dragging;
    endDocumentDrag();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: STATEMENT_FILE_ACCEPT,
    multiple: true,
  });

  const effectiveType = (doc: TrackedFileDocument): DocType =>
    pendingDocTypes[doc.id] ?? doc.doc_type ?? "unknown";

  const grouped = DOC_SECTIONS.map((section) => {
    const typeSet = new Set<string>(section.types);
    return {
      key: section.key,
      label: section.label,
      files: documents.filter((d) => typeSet.has(effectiveType(d))),
    };
  }).filter((g) => g.files.length > 0);

  const totalCount = documents.length + (plaidAssetReport?.visible ? 1 : 0);

  return (
    <Card className="flex h-full w-full flex-col">
      <CardContent className="flex flex-1 flex-col p-4">
        <div className="mb-3 flex items-center gap-2">
          <Upload className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Application Documents</h3>
          {totalCount > 0 && (
            <Badge variant="outline" className="ml-1">
              {totalCount}
            </Badge>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {uploadError && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{uploadError}</AlertDescription>
            </Alert>
          )}

          <div
            {...getRootProps()}
            className={cn(
              "mb-3 cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors",
              isDragActive ? "border-primary bg-muted/50" : "border-border"
            )}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm">
                  {isDragActive
                    ? "Drop the files here..."
                    : "Drag & drop files here, or click to select"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Supported formats: PDF, CSV, XLSX, XLS, OFX
                </p>
              </>
            )}
          </div>

          {grouped.map((group) => (
            <React.Fragment key={group.key}>
              <Separator className="my-3" />
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {group.label} ({group.files.length})
              </p>
              <ul className="space-y-2">
                {group.files.map((doc) => {
                  const docType = effectiveType(doc);
                  // Its requested name stands in for "Other" until staff pick a type.
                  const customLabel = doc.custom_label || null;
                  const showsCustom = customLabel != null && !(doc.id in pendingDocTypes);
                  const choice: DocTypeChoice = showsCustom ? CUSTOM_LABEL_OPTION : docType;
                  const bankStatementId =
                    docType === "bank_statement" ? (doc.bank_statement_id ?? null) : null;
                  const isBankStatement = bankStatementId != null;
                  const isMarkedForDelete =
                    isBankStatement && pendingDeletes.includes(bankStatementId);
                  const canView = !!doc.file_url && !isMarkedForDelete;
                  // Prefer statement processing status when present; otherwise
                  // TrackedFile status (signed contracts use ``processed``).
                  const status =
                    (isBankStatement ? doc.bank_statement_status : null) ??
                    doc.file_status ??
                    // A typed file with no status has nothing left to process.
                    (doc.doc_type && doc.doc_type !== "unknown" ? "processed" : "pending");
                  const statusLabel = displayStatus(status);
                  // The document as the chat takes it (H2's attach by reference).
                  const chatDocument: DraggedDocument | null =
                    canAddToChat && borrowerId != null && !isMarkedForDelete
                      ? {
                          kind: "document",
                          contextId: borrowerId,
                          id: doc.id,
                          name: doc.filename,
                          type: DOC_TYPE_LABELS[docType as DocType] ?? null,
                          url: doc.file_url ?? null,
                          statement: doc.bank_statement_id != null,
                        }
                      : null;
                  const draggable = chatDocument != null && finePointer;
                  return (
                    <li
                      key={doc.id}
                      className={cn(
                        "flex flex-wrap items-center gap-2 rounded-md border border-transparent px-1 py-1",
                        // A card dragged into the assistant's chat, lifted while it is picked up.
                        canAddToChat &&
                          "rounded-lg border-border bg-card px-2 py-1.5 data-[dragging]:opacity-50 data-[lifted]:shadow-lg motion-safe:transition-[box-shadow,opacity] motion-safe:duration-fade",
                        draggable && "cursor-grab active:cursor-grabbing",
                        isMarkedForDelete && "opacity-50"
                      )}
                      draggable={draggable || undefined}
                      onPointerDown={
                        draggable
                          ? (event) => {
                              pressedAction.current = isRowAction(event.target);
                            }
                          : undefined
                      }
                      onDragStart={
                        draggable ? (event) => startRowDrag(event, chatDocument) : undefined
                      }
                      onDragEnd={draggable ? endRowDrag : undefined}
                    >
                      {draggable && (
                        <GripVertical
                          data-row-grip=""
                          className="h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      {doc.file_url ? (
                        // The card's name: a click previews, a press-and-move drags the card (the
                        // browser starts a drag only past its threshold), so it is no row action.
                        <button
                          type="button"
                          className={cn(
                            "min-w-0 flex-1 truncate text-left text-sm",
                            canView && "cursor-pointer hover:text-primary hover:underline",
                            isMarkedForDelete && "line-through"
                          )}
                          title={doc.filename}
                          onClick={() => canView && onView?.(doc.id)}
                        >
                          {doc.filename}
                        </button>
                      ) : (
                        <span
                          className="min-w-0 flex-1 truncate text-left text-sm text-muted-foreground"
                          title={doc.filename}
                        >
                          {doc.filename}
                        </span>
                      )}
                      {chatDocument && (
                        // Keyboard and phone: the same attach, into the lip (opened if closed).
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          data-row-action=""
                          className="h-8 w-8 shrink-0 text-muted-foreground max-sm:h-10 max-sm:w-10"
                          aria-label={`Add ${doc.filename} to chat`}
                          onClick={() => addToLip(chatDocument)}
                        >
                          <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                      {/* A card keeps its name on the first line; its controls wrap to the next. */}
                      {canAddToChat && <div className="basis-full" aria-hidden="true" />}
                      <Select
                        value={choice}
                        onValueChange={(v) => onDocTypeChange?.(doc.id, v as DocTypeChoice)}
                        disabled={isMarkedForDelete || uploading}
                      >
                        <SelectTrigger
                          size="sm"
                          data-row-action=""
                          className="w-[180px] shrink-0 text-xs"
                        >
                          {/* The name alone; the Custom chip stays in the list. */}
                          <SelectValue>
                            {showsCustom && customLabel ? (
                              <CustomDocName name={customLabel} hint={false} />
                            ) : undefined}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {customLabel ? (
                            <>
                              <SelectItem
                                value={CUSTOM_LABEL_OPTION}
                                className="cursor-pointer text-xs"
                              >
                                <CustomDocName name={customLabel} hint={false} />
                                <Badge
                                  variant="outline"
                                  className="ml-2 px-1.5 py-0 text-[0.6rem] font-normal"
                                >
                                  Custom
                                </Badge>
                              </SelectItem>
                              <SelectSeparator />
                            </>
                          ) : null}
                          {DOC_TYPES.map((dt) => (
                            <SelectItem
                              key={dt}
                              value={dt}
                              className={cn("text-xs", customLabel && "cursor-pointer")}
                            >
                              {DOC_TYPE_LABELS[dt]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Badge
                        variant="secondary"
                        className={cn(
                          "shrink-0 text-[0.65rem]",
                          isMarkedForDelete
                            ? "bg-destructive/15 text-destructive"
                            : (STATUS_CLASS[status] ?? STATUS_CLASS[statusLabel] ?? "bg-muted")
                        )}
                      >
                        {isMarkedForDelete ? "will delete" : statusLabel}
                      </Badge>
                      {isBankStatement &&
                      !isMarkedForDelete &&
                      doc.bank_statement_status === "failed" ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          data-row-action=""
                          className="h-8 w-8 shrink-0 text-destructive"
                          title="Reload"
                          onClick={() => onReprocess?.(bankStatementId)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {isBankStatement ? (
                        isMarkedForDelete ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            data-row-action=""
                            className="h-8 w-8 shrink-0"
                            onClick={() => onRemovePendingDelete?.(bankStatementId)}
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            data-row-action=""
                            className="h-8 w-8 shrink-0 text-destructive"
                            onClick={() => onAddPendingDelete?.(bankStatementId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )
                      ) : (
                        <div className="h-8 w-8 shrink-0" aria-hidden />
                      )}
                    </li>
                  );
                })}
              </ul>
            </React.Fragment>
          ))}

          {plaidAssetReport?.visible && (
            <>
              <Separator className="my-3" />
              <p className="mb-2 text-xs font-medium text-muted-foreground">Plaid Reports (1)</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 rounded-md border border-transparent px-1 py-1">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {plaidAssetReport.fileUrl ? (
                    <button
                      type="button"
                      className="min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:text-primary hover:underline"
                      title="Plaid Asset Report.pdf"
                      onClick={() => plaidAssetReport.onView()}
                    >
                      {truncateFilename("Plaid Asset Report.pdf")}
                    </button>
                  ) : (
                    <span
                      className="min-w-0 flex-1 truncate text-left text-sm text-muted-foreground"
                      title="Plaid Asset Report.pdf"
                    >
                      {truncateFilename("Plaid Asset Report.pdf")}
                    </span>
                  )}
                  <Badge
                    variant="secondary"
                    className={cn(
                      "shrink-0 text-[0.65rem]",
                      plaidAssetReport.fileUrl
                        ? STATUS_CLASS.completed
                        : plaidAssetReport.retrying
                          ? STATUS_CLASS.processing
                          : plaidAssetReport.pdfFetchFailed
                            ? STATUS_CLASS.failed
                            : STATUS_CLASS.pending
                    )}
                  >
                    {plaidAssetReport.fileUrl
                      ? "completed"
                      : plaidAssetReport.retrying
                        ? "processing"
                        : plaidAssetReport.pdfFetchFailed
                          ? "failed"
                          : "pending"}
                  </Badge>
                  {plaidAssetReport.fileUrl ? (
                    <div className="h-8 w-8 shrink-0" aria-hidden />
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground"
                      onClick={() => plaidAssetReport.onRetry()}
                      disabled={plaidAssetReport.retrying}
                      aria-busy={plaidAssetReport.retrying}
                      title="Download PDF from Plaid"
                    >
                      {plaidAssetReport.retrying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </li>
              </ul>
            </>
          )}

          {pendingFiles.length > 0 && (
            <>
              <Separator className="my-3" />
              <p className="mb-2 text-xs font-medium text-muted-foreground">Files to Upload</p>
              <ul className="space-y-2">
                {pendingFiles.map((file, index) => (
                  <li key={index} className="flex items-center gap-2 px-1 py-1">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm" title={file.name}>
                        {truncateFilename(file.name)}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive"
                      onClick={() => onRemovePendingFile?.(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {hasPendingChanges && (
          <div className="mt-3 flex justify-end gap-2 border-t pt-3">
            <Button type="button" size="sm" variant="outline" onClick={onDiscard}>
              Cancel
            </Button>
            {borrowerId != null && (
              <Button type="button" size="sm" onClick={() => onSave?.()} disabled={uploading}>
                {uploading ? "Saving..." : "Save"}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AppFileManager;
